import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Text, Button, Dialog, UnderlineNav, IconButton } from "@primer/react";
import { MailIcon, DeviceMobileIcon, PersonIcon, MortarBoardIcon, PencilIcon } from "@primer/octicons-react";
import { phuHuynhApi } from "../../../api/phuHuynhApi";
import { hocSinhApi } from "../../../api/hocSinhApi";
import { quanHeGiaDinhApi } from "../../../api/quanHeGiaDinhApi";
import { chamSocKhachHangApi } from "../../../api/chamSocKhachHangApi";
import { useCommonContext } from "../../../contexts/common";
import { NotifyHelper } from "../../../helpers/toast";
import UserAvatar from "../../../components/user-avatar";
import PhuHuynhCSKHDetailModal from "../../../components/phu-huynh-cskh-item/detail-modal/PhuHuynhCSKHDetailModal";
import moment from "moment";
import DataTable from "../../../components/ui/data-table";

interface PhuHuynhData {
  id: number;
  ho_ten: string;
  email: string;
  dien_thoai: string;
  quan_he?: string;
}

interface FamilyMember {
  id: string | number;
  ho_ten: string;
  quan_he: string;
  email: string;
  dien_thoai: string;
}

interface HocSinhData {
  id: number;
  ho_ten: string;
  ma_hs: string;
  trang_thai: string;
  dm_gioitinh_id: number;
  gioi_tinh: string;
  ten_he: string;
  ten_lop: string;
}

interface CskhModalContentProps {
  id: string | number;
}

const formatDateTime = (date: any, formatStr: string = "DD/MM/YYYY") => {
  if (!date) return "";
  const m = moment(date);
  if (!m.isValid() || m.year() <= 1900) return "";
  return m.format(formatStr);
};

const getTagStyle = (quanHe?: string, type?: "parent" | "student") => {
  if (type === "student") {
    return { bg: "#f3ebfb", color: "#7c41c0" };
  }
  const relation = (quanHe || "").toLowerCase();
  if (relation.includes("mẹ") || relation.includes("me")) {
    return { bg: "#e4f4ec", color: "#147a52" };
  }
  if (relation.includes("bố") || relation.includes("bo") || relation.includes("cha")) {
    return { bg: "#e8f0fe", color: "#2256c8" };
  }
  return { bg: "#e4f4ec", color: "#147a52" };
};

const PersonCard: React.FC<{
  name: string;
  tag?: string;
  tagStyle: { bg: string; color: string };
  metaLines: { icon: React.ReactNode; text: string }[];
  isActive?: boolean;
  onClick?: () => void;
}> = ({ name, tag, tagStyle, metaLines, isActive, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      display: "flex",
      gap: "11px",
      alignItems: "center",
      padding: "11px 10px",
      borderRadius: "11px",
      cursor: onClick ? "pointer" : "default",
      transition: "0.14s",
      position: "relative",
      backgroundColor: isActive ? "#eaf0fe" : "transparent",
      "&:hover": {
        backgroundColor: isActive ? "#eaf0fe" : onClick ? "#f6f7f9" : "transparent",
      },
    }}
  >

    {}
    <UserAvatar fullName={name} size="large" />

    {}
    <Box sx={{ minWidth: 0, flex: 1 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "7px", mb: "3px" }}>
        <Text
          sx={{
            fontSize: "14px",
            fontWeight: 600,
            color: "fg.default",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {name}
        </Text>
        {tag && (
          <Text
            as="span"
            sx={{
              fontSize: "11px",
              fontWeight: 600,
              padding: "1px 8px",
              borderRadius: "20px",
              flexShrink: 0,
              backgroundColor: tagStyle.bg,
              color: tagStyle.color,
            }}
          >
            {tag}
          </Text>
        )}
      </Box>

      {}
      <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
        {metaLines
          .filter((m) => m.text)
          .map((meta, i) => (
            <Box key={i} sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <Box sx={{ color: "fg.muted", flexShrink: 0, fontSize: "12px" }}>{meta.icon}</Box>
              <Text
                sx={{
                  fontSize: "12px",
                  color: "fg.muted",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {meta.text}
              </Text>
            </Box>
          ))}
      </Box>
    </Box>
  </Box>
);

const SimpleComposer: React.FC<{
  phuHuynhId: number;
  phuHuynhName: string;
  phuHuynhPhone: string;
  onSuccess: () => void;
}> = ({ phuHuynhId, phuHuynhName, phuHuynhPhone, onSuccess }) => {
  const [noiDung, setNoiDung] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async () => {
    if (!noiDung.trim()) {
      NotifyHelper.Error("Vui lòng nhập nội dung chăm sóc");
      return;
    }
    try {
      setIsLoading(true);
      const res = await chamSocKhachHangApi.insert({
        id: 0,
        dien_thoai: phuHuynhPhone,
        ten_khach_hang: phuHuynhName,
        noi_dung: noiDung.trim(),
        ts_hocsinh_id: 0,
        da_xu_ly: false,
        ngay_xu_ly: null,
        user_xu_ly_id: 0,
        ghi_chu: "",
        ts_phuhuynh_id: phuHuynhId,
        ts_hoso_survey_id: 0,
        ket_qua_xu_ly: "",
        is_request: false,
        ngay_hen_tra_loi: null,
        ngayHenTraLoiText: "",
        tsPhuHuynhAdmStatusId: 0,
      });
      if (res.is_success) {
        NotifyHelper.Success("Thêm mới thành công");
        setNoiDung("");
        onSuccess();
      } else {
        NotifyHelper.Error("Có lỗi xảy ra");
      }
    } catch {
      NotifyHelper.Error("Có lỗi xảy ra");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: "border.default",
        borderRadius: "12px",
        overflow: "hidden",
        transition: "0.18s",
        "&:focus-within": {
          borderColor: "#2b5ce6",
          boxShadow: "0 0 0 3px #eaf0fe",
        },
      }}
    >
      <Text
        sx={{
          display: "block",
          fontSize: "12px",
          fontWeight: 600,
          color: "fg.muted",
          padding: "11px 15px 0",
        }}
      >
        Thêm ghi chú chăm sóc · {phuHuynhName}
      </Text>
      <Box
        as="textarea"
        value={noiDung}
        onChange={(e: any) => setNoiDung(e.target.value)}
        placeholder="Nhập nội dung chăm sóc…"
        sx={{
          width: "100%",
          border: "none",
          background: "transparent",
          resize: "none",
          fontFamily: "inherit",
          fontSize: "13.5px",
          color: "fg.default",
          padding: "7px 15px 4px",
          lineHeight: 1.5,
          height: "54px",
          outline: "none",
          "&:focus": { height: "88px" },
          "&::placeholder": { color: "fg.subtle" },
        }}
      />
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: 2,
          padding: "9px 12px 11px",
          borderTop: "1px solid",
          borderColor: "border.muted",
        }}
      >
        <Button
          variant="invisible"
          onClick={() => setNoiDung("")}
          disabled={isLoading}
          sx={{ color: "fg.muted", fontWeight: 600 }}
        >
          Hủy
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={isLoading || !noiDung.trim()}
          sx={{
            fontWeight: 600,
            bg: "#2b5ce6",
            "&:hover": { bg: "#2350cf" },
          }}
        >
          Lưu ghi chú
        </Button>
      </Box>
    </Box>
  );
};

const TimelineHistory: React.FC<{
  ts_phuhuynh_id: number;
  ts_hocsinh_id: number;
  ts_hoso_survey_id: number;
  changedCount: number;
  onChanged?: () => void;
}> = ({ ts_phuhuynh_id, ts_hocsinh_id, ts_hoso_survey_id, changedCount, onChanged }) => {
  const [items, setItems] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editId, setEditId] = useState<number | null>(null);

  useEffect(() => {
    loadData();
  }, [ts_phuhuynh_id, ts_hocsinh_id, ts_hoso_survey_id, changedCount]);

  const loadData = async () => {
    try {
      setIsLoading(true);
      const res = await chamSocKhachHangApi.select({
        ts_phuhuynh_id,
        ts_hocsinh_id,
        ts_hoso_survey_id,
      });
      if (res.is_success && Array.isArray(res.data)) {
        setItems(res.data);
      } else {
        setItems([]);
      }
    } catch {
      setItems([]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <DataTable
        height="calc(100vh - 555px)"
        data={items}
        isLoading={isLoading}
        columns={[
          {
            id: "action",
            width: "50px",
            caption: "#",
            align: "center",
            cellRender: (row: any) => (
              <IconButton
                icon={PencilIcon}
                variant="invisible"
                size="small"
                onClick={() => setEditId(row.id)}
                aria-label="Chỉnh sửa"
              />
            ),
          },
          {
            dataField: "da_xu_ly",
            width: "110px",
            caption: "Trạng thái",
            cellRender: (row: any) => {
              const isResolved = row.da_xu_ly;
              return (
                <Text
                  as="span"
                  sx={{
                    fontSize: "11.5px",
                    fontWeight: 600,
                    padding: "2px 9px",
                    borderRadius: "20px",
                    display: "inline-block",
                    ...(isResolved
                      ? { color: "#0f8a5b", bg: "#e6f4ee" }
                      : { color: "#bf8700", bg: "#fff8c5" }),
                  }}
                >
                  {isResolved ? "Đã xử lý" : "Chưa xử lý"}
                </Text>
              );
            },
          },
          {
            dataField: "is_request",
            width: "110px",
            caption: "Phân loại",
            cellRender: (row: any) => (
              <Text
                as="span"
                sx={{
                  fontSize: "11.5px",
                  fontWeight: 500,
                  padding: "2px 9px",
                  borderRadius: "20px",
                  display: "inline-block",
                  bg: row.is_request ? "#eaf0fe" : "neutral.subtle",
                  color: row.is_request ? "#2b5ce6" : "fg.muted",
                }}
              >
                {row.is_request ? "Yêu cầu PH" : "Ghi chú TS"}
              </Text>
            ),
          },
          {
            dataField: "noi_dung",
            caption: "Nội dung chăm sóc",
            width: "250px",
          },
          {
            dataField: "ket_qua_xu_ly",
            caption: "Kết quả xử lý",
            width: "180px",
          },
          {
            dataField: "ghi_chu",
            caption: "Ghi chú thêm",
            width: "150px",
          },
          {
            dataField: "ho_ten_hoc_sinh",
            caption: "Học sinh",
            width: "130px",
          },
          {
            dataField: "nguoi_tao",
            caption: "Người tạo",
            width: "120px",
          },
          {
            dataField: "ngay_tao",
            caption: "Ngày tạo",
            width: "140px",
            cellRender: (row: any) => (
              <Text>{formatDateTime(row.ngay_tao, "DD/MM/YYYY HH:mm")}</Text>
            ),
          },
          {
            dataField: "ngay_hen_tra_loi",
            caption: "Hẹn trả lời",
            width: "110px",
            cellRender: (row: any) => (
              <Text>{formatDateTime(row.ngay_hen_tra_loi, "DD/MM/YYYY")}</Text>
            ),
          },
          {
            dataField: "ngay_xu_ly",
            caption: "Ngày xử lý",
            width: "140px",
            cellRender: (row: any) => (
              <Text>{formatDateTime(row.ngay_xu_ly, "DD/MM/YYYY HH:mm")}</Text>
            ),
          },
        ]}
      />

      {editId && (
        <PhuHuynhCSKHDetailModal
          id={editId}
          onCancel={() => setEditId(null)}
          onSuccess={() => {
            setEditId(null);
            loadData();
            onChanged?.();
          }}
        />
      )}
    </>
  );
};

const TimelineDangKy: React.FC<{ ts_phuhuynh_id: number }> = ({ ts_phuhuynh_id }) => {
  const [items, setItems] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [ts_phuhuynh_id]);

  const loadData = async () => {
    try {
      setIsLoading(true);
      const res = await phuHuynhApi.selectLichSuDangKy(ts_phuhuynh_id);
      if (res.is_success) {
        setItems(res.data);
      } else {
        setItems([]);
      }
    } catch {
      setItems([]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <DataTable
      height="calc(100vh - 550px)"
      data={items}
      isLoading={isLoading}
      columns={[
        {
          dataField: "ten_chien_dich",
          caption: "Chiến dịch",
          width: "200px",
          cellRender: (row: any) => (
            <Text
              as="span"
              sx={{
                fontSize: "11.5px",
                fontWeight: 600,
                color: "#2b5ce6",
                bg: "#eaf0fe",
                padding: "2px 9px",
                borderRadius: "20px",
                display: "inline-block",
              }}
            >
              {row.ten_chien_dich}
            </Text>
          ),
        },
        {
          dataField: "register_date",
          caption: "Ngày đăng ký",
          width: "120px",
          cellRender: (row: any) => (
            <Text>{formatDateTime(row.register_date, "DD/MM/YYYY")}</Text>
          ),
        },
        {
          dataField: "ho_ten_hoc_sinh",
          caption: "Học sinh",
          width: "150px",
        },
        {
          dataField: "khoi_lop",
          caption: "Khối lớp",
          width: "100px",
        },
        {
          dataField: "ghi_chu",
          caption: "Ghi chú",
          width: "250px",
        },
      ]}
    />
  );
};

const SectionLabel: React.FC<{ children: React.ReactNode; mt?: number }> = ({
  children,
  mt = 0,
}) => (
  <Text
    sx={{
      fontSize: "11px",
      fontWeight: 600,
      letterSpacing: "0.06em",
      textTransform: "uppercase",
      color: "fg.muted",
      display: "block",
      mt,
      mb: 2,
      px: "6px",
    }}
  >
    {children}
  </Text>
);

const CskhModalContent: React.FC<CskhModalContentProps> = ({ id }) => {
  const { translate } = useCommonContext();

  const [phuHuynh, setPhuHuynh] = useState<PhuHuynhData | null>(null);
  const [familyMembers, setFamilyMembers] = useState<FamilyMember[]>([]);
  const [children, setChildren] = useState<HocSinhData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const componentDidMount = useRef(false);

  const [selectedPhuHuynhId, setSelectedPhuHuynhId] = useState<number>(Number(id));
  const [selectedHocSinhId, setSelectedHocSinhId] = useState<number>(0);
  const [selectedHoSoSurveyId, setSelectedHoSoSurveyId] = useState<number>(0);

  const [selectedTabId, setSelectedTabId] = useState<number>(0);
  const [changedCount, setChangedCount] = useState<number>(0);

  const [deleteState, setDeleteState] = useState<{
    isOpen: boolean;
    phuHuynhId: number;
    hocSinhId: number;
  }>({ isOpen: false, phuHuynhId: 0, hocSinhId: 0 });

  useEffect(() => {
    componentDidMount.current = true;
    loadData();
    return () => {
      componentDidMount.current = false;
    };
  }, [selectedPhuHuynhId]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [phRes, famRes, hsRes] = await Promise.all([
        phuHuynhApi.getById(selectedPhuHuynhId),
        phuHuynhApi.selectFamily(selectedPhuHuynhId),
        hocSinhApi.SelectByParent(selectedPhuHuynhId),
      ]);

      if (!componentDidMount.current) return;

      if (phRes.is_success) setPhuHuynh(phRes.data);
      if (famRes.is_success) setFamilyMembers(famRes.data);
      if (hsRes.is_success) setChildren(hsRes.data);
    } catch (error) {
      console.error("Error loading modal data:", error);
    } finally {
      if (componentDidMount.current) setIsLoading(false);
    }
  };

  const handleFamilyMemberSelect = useCallback((memberId: number) => {
    setSelectedPhuHuynhId(memberId);
    setSelectedHocSinhId(0);
    setSelectedHoSoSurveyId(0);
  }, []);

  const handleChildSelect = useCallback((hocSinhId: number) => {
    setSelectedHocSinhId(hocSinhId);
    setSelectedHoSoSurveyId(-1);
  }, []);

  const handleHistoryChange = useCallback(() => {
    setChangedCount((prev) => prev + 1);
  }, []);

  const handleDeleteQuanHe = useCallback(
    async () => {
      try {
        const response = await quanHeGiaDinhApi.deleteQHGD({
          phuHuynhId: deleteState.phuHuynhId,
          hocSinhId: deleteState.hocSinhId,
        });
        if (response.is_success) {
          NotifyHelper.Success(translate("Success"));
          if (deleteState.hocSinhId === selectedHocSinhId) {
            setSelectedHocSinhId(0);
            setSelectedHoSoSurveyId(0);
          }
          loadData();
        } else {
          NotifyHelper.Error(response.message || translate("Error occurred"));
        }
      } catch (error) {
        NotifyHelper.Error(translate("Error occurred"));
      } finally {
        setDeleteState({ isOpen: false, phuHuynhId: 0, hocSinhId: 0 });
      }
    },
    [deleteState, selectedHocSinhId, translate]
  );

  const containerHeight = useMemo(() => window.innerHeight - 300, []);

  const tabs = [
    { id: 0, text: "Lịch sử chăm sóc" },
    { id: 1, text: "Lịch sử đăng ký" },
  ];

  return (
    <Box
      sx={{
        display: "flex",
        height: containerHeight,
        maxWidth: "100%",
      }}
    >
      {}
      <Box
        sx={{
          width: "350px",
          flexShrink: 0,
          overflow: "auto",
          borderRight: "1px solid",
          borderColor: "border.default",
          pr: 2
        }}
      >
        {}
        <SectionLabel>Người liên hệ</SectionLabel>
        {phuHuynh && (
          <PersonCard
            name={phuHuynh.ho_ten}
            tag={phuHuynh.quan_he}
            tagStyle={getTagStyle(phuHuynh.quan_he)}
            isActive={true}
            metaLines={[
              { icon: <MailIcon size={12} />, text: phuHuynh.email },
              { icon: <DeviceMobileIcon size={12} />, text: phuHuynh.dien_thoai },
            ]}
          />
        )}

        {}
        {familyMembers.length > 0 && (
          <>
            <SectionLabel mt={3}>Thành viên khác</SectionLabel>
            {familyMembers.map((member, idx) => (
              <PersonCard
                key={idx}
                name={member.ho_ten}
                tag={member.quan_he}
                tagStyle={getTagStyle(member.quan_he)}
                onClick={() => handleFamilyMemberSelect(Number(member.id))}
                metaLines={[
                  { icon: <MailIcon size={12} />, text: member.email },
                  { icon: <DeviceMobileIcon size={12} />, text: member.dien_thoai },
                ]}
              />
            ))}
          </>
        )}

        {}
        {children.length > 0 &&
          children.map((child, idx) => (
            <PersonCard
              key={idx}
              name={child.ho_ten}
              tag="Học sinh"
              tagStyle={getTagStyle(undefined, "student")}
              isActive={selectedHocSinhId === child.id}
              onClick={() => handleChildSelect(child.id)}
              metaLines={[
                { icon: <PersonIcon size={12} />, text: child.ma_hs },
                {
                  icon: <MortarBoardIcon size={12} />,
                  text: [child.ten_he, child.ten_lop].filter(Boolean).join(" · "),
                },
              ]}
            />
          ))}
      </Box>

      {}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minWidth: 0,
          overflow: "auto",
        }}
      >
        {}
        <Box sx={{ mb: 2, px: 3, pt: 2 }}>
          <SimpleComposer
            phuHuynhId={selectedPhuHuynhId}
            phuHuynhName={phuHuynh?.ho_ten || ""}
            phuHuynhPhone={phuHuynh?.dien_thoai || ""}
            onSuccess={handleHistoryChange}
          />
        </Box>

        {}
        <Box>
          <UnderlineNav aria-label="Main">
            {tabs.map((tab) => (
              <UnderlineNav.Item
                key={tab.id}
                aria-current={selectedTabId === tab.id ? "page" : undefined}
                onSelect={(
                  event:
                    | React.MouseEvent<HTMLAnchorElement>
                    | React.KeyboardEvent<HTMLAnchorElement>
                ) => {
                  event.preventDefault();
                  setSelectedTabId(tab.id);
                }}
                sx={{
                  fontWeight: selectedTabId === tab.id ? 600 : 400,
                }}
              >
                {tab.text}
              </UnderlineNav.Item>
            ))}
          </UnderlineNav>

          <Box sx={{ padding: "18px 22px 22px", overflowY: "auto" }}>
            {selectedTabId === 0 ? (
              <TimelineHistory
                ts_phuhuynh_id={selectedPhuHuynhId}
                ts_hocsinh_id={selectedHocSinhId}
                ts_hoso_survey_id={selectedHoSoSurveyId}
                changedCount={changedCount}
                onChanged={handleHistoryChange}
              />
            ) : (
              <TimelineDangKy ts_phuhuynh_id={selectedPhuHuynhId} />
            )}
          </Box>
        </Box>
      </Box>

      {}
      <Dialog
        isOpen={deleteState.isOpen}
        onDismiss={() => setDeleteState({ isOpen: false, phuHuynhId: 0, hocSinhId: 0 })}
        title={translate("Notice")}
      >
        <Box p={3}>
          <Box mb={3}>{translate("Are you sure you want to delete this student?")}</Box>
          <Box display="flex" justifyContent="flex-end">
            <Button
              onClick={() => setDeleteState({ isOpen: false, phuHuynhId: 0, hocSinhId: 0 })}
              sx={{ mr: 2 }}
            >
              {translate("Close")}
            </Button>
            <Button variant="danger" onClick={handleDeleteQuanHe}>
              {translate("Delete Student")}
            </Button>
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
};

export default CskhModalContent;
